import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { useSendoutAccountability } from 'features/sendouts/hooks';
import { useMountedSafeState } from 'features/sendouts/hooks/useMountedSafeState';
import { useSendoutCandidateHydration } from 'features/sendouts/hooks/useSendoutCandidateHydration';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { EntityRoutes } from 'routes/constants';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import HighlightBox from 'UI/components/molecules/HighlightBox';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyEntity, CompanyType, JobOrderEntity } from 'UI/constants/entityTypes';
import { UIStatus } from 'UI/constants/status';
import { EmptyFeeAgreement } from 'UI/res';

import { JobOrderDetails } from '../../shared';
import { JOB_ORDER_INCLUDES } from '../CreateSendoutDrawer/CreateSendoutDrawer.constants';

import { LoaderContainer } from './CreateSendoverDrawer.styled';
import { buildSendoverSubmissionData, getSelectedAttachments } from './CreateSendoverDrawer.utils';
import { SendoverForm } from './SendoverForm';

const CONFIRMATION_DIALOGS = {
  NoAttachments: 'noAttachments',
  Preview: 'preview'
};

export const CreateSendoverDrawer = ({
  jobOrderId = null,
  onClose = () => {},
  onSaved = () => {},
  onSaveAsDraft = () => {},
  onViewDuplicates = () => {}
}) => {
  const form = useForm();
  const history = useHistory();

  const { handleSubmit } = form;

  const [candidateDuplicateInfo, setCandidateDuplicateInfo] = useState({
    isDuplicate: false,
    duplicates: [],
    error: null,
    shouldBlock: false
  });
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [activeDialog, setActiveDialog] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [isSaving, setIsSaving] = useMountedSafeState(false);
  const { candidateAttachments, hydrateCandidate, isHydratingCandidate, recruiterOptions } =
    useSendoutCandidateHydration({
      jobOrderId,
      onCandidateInfoChange: setCandidateInfo,
      onDuplicateChange: setCandidateDuplicateInfo
    });

  const { state } = useFetchWithStatusV2({
    endpoint: `${Endpoints.JobOrders}/${jobOrderId}?includes=${JOB_ORDER_INCLUDES}`,
    apiVersion: 2
  });

  const jobOrder = state.results || {};
  const { recruiter, coach, additionalRecruiters, company } = jobOrder;

  const isCompanySignedOrClient = [CompanyType.Client, CompanyType.Vendor].includes(
    company?.type?.id
  );

  const { canUserCreateSendout } = useSendoutAccountability({
    recruiter,
    coach,
    additionalRecruiters
  });
  const canShowForm = canUserCreateSendout && isCompanySignedOrClient;

  const isFetching = state.status === UIStatus.Loading;
  const hasErrorInInitialFetching = state.status === UIStatus.Error;

  const sendoverInfo = {
    jobOrder: {
      id: state.results?.id,
      title: JobOrderEntity.singular,
      name: state.results?.title
    },
    company: {
      id: state.results?.company?.id,
      title: CompanyEntity.singular,
      name: state.results?.company?.name
    }
  };

  const handleSave = formData => {
    const nextSubmissionData = buildSendoverSubmissionData({
      formData,
      jobOrder,
      candidateInfo
    });
    const selectedAttachments = getSelectedAttachments(formData);
    const hasNoAttachments = selectedAttachments.length === 0;

    setSubmissionData(nextSubmissionData);

    if (hasNoAttachments) {
      setActiveDialog(CONFIRMATION_DIALOGS.NoAttachments);
      return;
    }

    setActiveDialog(CONFIRMATION_DIALOGS.Preview);
  };

  const closeConfirmationFlow = () => {
    setActiveDialog(null);
    setSubmissionData(null);
  };

  const handleConfirmNoAttachments = () => {
    setActiveDialog(CONFIRMATION_DIALOGS.Preview);
  };

  const handleViewCompanyProfile = () => {
    if (!company?.id) return;

    history.push(EntityRoutes.CompanyProfile.replace(':id', company.id));
  };

  const handleConfirmPreview = async () => {
    if (!submissionData) return;

    const nextSubmissionData = submissionData;
    setIsSaving(true);
    try {
      const result = await onSaved(nextSubmissionData);

      if (result?.success !== false) {
        closeConfirmationFlow();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isSaveDisabled = [
    isFetching,
    isHydratingCandidate,
    isSaving,
    hasErrorInInitialFetching,
    candidateDuplicateInfo.shouldBlock
  ].some(Boolean);

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: onClose,
        disabled: isSaving
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.saveAsDraft,
        variant: 'outlined',
        onClick: onSaveAsDraft,
        disabled: isFetching || isSaving || hasErrorInInitialFetching
      },
      visible: canShowForm,
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.save,
        variant: 'contained',
        onClick: handleSubmit(handleSave),
        disabled: isSaveDisabled,
        isSaving
      },
      visible: canShowForm,
      GridItemProps: { md: 3 },
      mode: 'save'
    }
  ];

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true }}
        CardHeaderProps={{ title: strings.sendoutsV2.drawers.sendover.title }}
        onClose={onClose}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <When condition={isFetching}>
          <LoaderContainer>
            <CircularProgress />
          </LoaderContainer>
        </When>
        <When condition={!isFetching}>
          <When condition={!hasErrorInInitialFetching && !canUserCreateSendout}>
            <EmptyPlaceholder
              subtitle={strings.formatString(strings.sendoutsV2.form.emptyPlaceholderText, {
                documentType: 'Sendover'
              })}
              height="100%"
            />
          </When>
          <When condition={hasErrorInInitialFetching}>
            <EmptyPlaceholder
              subtitle={strings.formatString(strings.sendoutsV2.form.initialFetchingError, {
                documentType: 'Sendover'
              })}
              height="100%"
            />
          </When>

          <When condition={state.status === UIStatus.Success && !isCompanySignedOrClient}>
            <EmptyPlaceholder
              title={strings.sendoutsV2.alerts.noSignedCompany.title}
              subtitle={strings.sendoutsV2.alerts.noSignedCompany.description}
              customEmptyState={<EmptyFeeAgreement />}
            >
              <FPActionButton
                text={strings.sendoutsV2.alerts.noSignedCompany.signCompany}
                type="button"
                onClick={handleViewCompanyProfile}
              />
            </EmptyPlaceholder>
          </When>

          <When condition={canShowForm && state.status === UIStatus.Success}>
            <FormContext {...form}>
              <JobOrderDetails company={sendoverInfo.company} jobOrder={sendoverInfo.jobOrder} />
              <SendoverForm
                candidateAttachments={candidateAttachments}
                candidateInfo={candidateInfo}
                duplicateInfo={candidateDuplicateInfo}
                hydrateCandidate={hydrateCandidate}
                isHydratingCandidate={isHydratingCandidate}
                jobOrder={jobOrder}
                recruiterOptions={recruiterOptions}
                onViewDuplicates={onViewDuplicates}
              />
            </FormContext>
          </When>
        </When>
      </FPDrawer>
      <When condition={activeDialog === CONFIRMATION_DIALOGS.NoAttachments}>
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.send}
          isOpened
          onClose={closeConfirmationFlow}
          onConfirm={handleConfirmNoAttachments}
          severity="warning"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.noAttachments.title}
          withButtons="YesNo"
          message={strings.sendoutsV2.alerts.confirmationDialogs.noAttachments.message}
        />
      </When>
      <When condition={activeDialog === CONFIRMATION_DIALOGS.Preview}>
        <DecisionDialog
          cancelButtonText={strings.shared.ui.cancel}
          confirmButtonText={strings.shared.ui.singleConfirm}
          isOpened
          mustComplete
          onClose={closeConfirmationFlow}
          onConfirm={handleConfirmPreview}
          severity="info"
          showCloseButton={false}
          showSeverityIcon
          title={strings.sendoutsV2.alerts.confirmationDialogs.emailConfirmation.title}
          message={strings.sendoutsV2.alerts.confirmationDialogs.emailConfirmation.message}
          withButtons="YesNo"
        >
          <HighlightBox
            text={submissionData?.confirmation?.resolvedEmailBody || ''}
            highlightData={submissionData?.confirmation?.resolvedTagValues || []}
          />
        </DecisionDialog>
      </When>
    </>
  );
};
